/**
 * Custom Hook: useWelcomeSpeech
 * Speaks a spoken welcome message shortly after the website loads, using the
 * browser's built-in Web Speech API (SpeechSynthesis). The message tells the
 * user they can change the language via the language button, and is localized
 * for English, Hindi and Gujarati.
 *
 * Timing:
 * - An auto attempt fires after `delay` ms so it lines up with the hero text
 *   animation finishing (heading + description settle around ~1.1s).
 *
 * Autoplay note:
 * - Browsers (Chrome/Edge/Safari) block audio until the first user gesture. If
 *   the auto attempt is blocked the engine fires an 'onerror' ("not-allowed")
 *   instead of playing — so we DO NOT mark the greeting as spoken until the
 *   utterance actually starts (onstart). Meanwhile a gesture fallback stays
 *   armed (click / key / touch / scroll), which both unlocks the engine and
 *   plays the greeting. This guarantees the message plays at the latest on the
 *   very first interaction.
 * - Spoken at most once per page load.
 *
 * @param {string} language - Current language code ('en' | 'hi' | 'gu')
 * @param {number} [delay=1400] - Delay in ms before the auto greeting attempt
 * @returns {boolean} isSpeaking - true while the welcome message is being spoken
 */
import { useEffect, useRef, useState } from 'react';

// Localized welcome scripts + the BCP-47 lang tag used to pick a matching voice.
const WELCOME_MESSAGES = {
  en: {
    lang: 'en-IN',
    text:
      'Welcome to CropShield. You can change the language by clicking the language button. ' +
      'English, Hindi and Gujarati are available.',
  },
  hi: {
    lang: 'hi-IN',
    text:
      'क्रॉपशील्ड में आपका स्वागत है। आप भाषा बटन पर क्लिक करके भाषा बदल सकते हैं। ' +
      'अंग्रेज़ी, हिन्दी और गुजराती उपलब्ध हैं।',
  },
  gu: {
    lang: 'gu-IN',
    text:
      'ક્રૉપશીલ્ડમાં આપનું સ્વાગત છે. તમે ભાષા બટન પર ક્લિક કરીને ભાષા બદલી શકો છો. ' +
      'અંગ્રેજી, હિન્દી અને ગુજરાતી ઉપલબ્ધ છે.',
  },
};

// Pick the best available voice for a given BCP-47 language tag.
const pickVoice = (voices, langTag) => {
  if (!voices || voices.length === 0) return null;
  const base = langTag.split('-')[0].toLowerCase();
  return (
    voices.find((v) => v.lang?.toLowerCase() === langTag.toLowerCase()) ||
    voices.find((v) => v.lang?.toLowerCase().startsWith(base)) ||
    null
  );
};

export const useWelcomeSpeech = (language = 'en', delay = 1400) => {
  // Persist "already greeted" across StrictMode's double-mount in dev.
  const hasSpokenRef = useRef(false);
  const attemptingRef = useRef(false);
  // Drives the language-button highlight ring while the greeting plays.
  const [isSpeaking, setIsSpeaking] = useState(false);

  useEffect(() => {
    if (hasSpokenRef.current) return;
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    const synth = window.speechSynthesis;
    const message = WELCOME_MESSAGES[language] || WELCOME_MESSAGES.en;

    let timerId = null;
    const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'];

    const cleanupListeners = () => {
      events.forEach((evt) => window.removeEventListener(evt, onGesture));
      synth.removeEventListener('voiceschanged', speak);
    };

    const speak = () => {
      // Bail if we've already succeeded or an attempt is currently in flight.
      if (hasSpokenRef.current || attemptingRef.current) return;

      const voices = synth.getVoices();
      // Voices can load lazily; wait for them then retry.
      if (voices.length === 0) {
        synth.addEventListener('voiceschanged', speak, { once: true });
        return;
      }

      const utterance = new window.SpeechSynthesisUtterance(message.text);
      utterance.lang = message.lang;
      utterance.rate = 0.95;
      utterance.pitch = 1;
      utterance.volume = 1;
      const voice = pickVoice(voices, message.lang);
      if (voice) utterance.voice = voice;

      // Lock in success only once audio truly begins — a blocked autoplay
      // attempt fires onerror instead, leaving the gesture fallback armed.
      utterance.onstart = () => {
        hasSpokenRef.current = true;
        attemptingRef.current = false;
        setIsSpeaking(true); // highlight the language button
        if (timerId) window.clearTimeout(timerId);
        cleanupListeners();
      };
      utterance.onend = () => {
        attemptingRef.current = false;
        setIsSpeaking(false); // sentence finished — remove highlight
      };
      utterance.onerror = () => {
        // Blocked (e.g. "not-allowed") or interrupted — stay armed for a gesture.
        attemptingRef.current = false;
        setIsSpeaking(false);
      };

      attemptingRef.current = true;
      synth.cancel(); // clear any stale/blocked queue first
      synth.resume(); // work around Chrome's paused-engine quirk
      synth.speak(utterance);
    };

    // A real user gesture is always allowed to play audio.
    const onGesture = () => speak();
    events.forEach((evt) =>
      window.addEventListener(evt, onGesture, { passive: true })
    );

    // Auto attempt once the hero text animation has settled.
    timerId = window.setTimeout(speak, delay);

    return () => {
      if (timerId) window.clearTimeout(timerId);
      cleanupListeners();
      // Stop any in-progress speech if the app unmounts.
      if (synth.speaking || synth.pending) synth.cancel();
    };
    // Run once on mount; the greeting must not repeat on language switches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isSpeaking;
};
